/**
 * CombatLogPanel — Persistent scrollable log of all DebugFCT messages.
 *
 * Shows the full pipeline trace: cast → VFX → combat → network.
 * Filterable by channel. "Copy Log" copies last 50 entries to clipboard.
 * Auto-scrolls to bottom. Uses ToolWindow for drag/resize.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ToolWindow } from '../components/ToolWindow';
import { DebugFCT, type DebugFCTEntry, type FCTChannel } from '../../debug/DebugFCT';
import { gameLogger } from '../../../utils/GameLogger';

const CHANNEL_COLORS: Record<FCTChannel, string> = {
  cast: '#e0e0ff',
  vfx: '#74c0fc',
  combat: '#ff6b6b',
  network: '#a0a0a0',
};

const CHANNEL_LABELS: Record<FCTChannel, string> = {
  cast: 'CAST',
  vfx: 'VFX',
  combat: 'DMG',
  network: 'NET',
};

const LEVEL_SYMBOLS = {
  success: '\u2713',
  fail: '\u2717',
  info: '\u00B7',
} as const;

const LEVEL_COLORS = {
  success: '#51cf66',
  fail: '#ff6b6b',
  info: '#74c0fc',
} as const;

// Frame-jitter Wave 1 Phase C (2026-05-16) \u2014 hoist inline styles so each
// log-row render reuses these constants instead of allocating fresh
// `style={{...}}` objects. Per-row we were creating 5 fresh objects \u00D7
// up to 200 rows \u00D7 N renders/sec, which the CPU profile showed as the
// dominant allocator (82.88% of self-time in debug mode).
const ROW_OUTER_STYLE: React.CSSProperties = { display: 'flex', gap: 4, padding: '1px 0' };
const TIME_STYLE: React.CSSProperties = { color: '#555', flexShrink: 0 };
const LEVEL_BASE_STYLE: React.CSSProperties = { flexShrink: 0, width: 10, textAlign: 'center' };
const CHANNEL_BASE_STYLE: React.CSSProperties = { flexShrink: 0, width: 32, textAlign: 'right' };
const TEXT_BASE_STYLE: React.CSSProperties = { wordBreak: 'break-word' };
const TEXT_FAIL_COLOR = '#ff6b6b';
const TEXT_OK_COLOR = '#ccc';

const TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
});

interface LogRowProps {
  entry: DebugFCTEntry;
}

const LogRow = React.memo<LogRowProps>(({ entry }) => {
  // Format once per entry. The entry object is referentially stable across
  // renders (it's the same object pushed once into the entries array), so
  // React.memo's shallow-equal lets us skip every re-render of an already-
  // displayed row.
  const time = useMemo(() => TIME_FMT.format(entry.time), [entry.time]);
  const channelStyle = useMemo<React.CSSProperties>(
    () => ({ ...CHANNEL_BASE_STYLE, color: CHANNEL_COLORS[entry.channel] }),
    [entry.channel],
  );
  const levelStyle = useMemo<React.CSSProperties>(
    () => ({ ...LEVEL_BASE_STYLE, color: LEVEL_COLORS[entry.level] }),
    [entry.level],
  );
  const textStyle = useMemo<React.CSSProperties>(
    () => ({ ...TEXT_BASE_STYLE, color: entry.level === 'fail' ? TEXT_FAIL_COLOR : TEXT_OK_COLOR }),
    [entry.level],
  );
  return (
    <div style={ROW_OUTER_STYLE}>
      <span style={TIME_STYLE}>{time}</span>
      <span style={channelStyle}>{CHANNEL_LABELS[entry.channel]}</span>
      <span style={levelStyle}>{LEVEL_SYMBOLS[entry.level]}</span>
      <span style={textStyle}>{entry.text}</span>
    </div>
  );
});
LogRow.displayName = 'LogRow';

interface CombatLogPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ALL_CHANNELS: FCTChannel[] = ['cast', 'vfx', 'combat', 'network'];

export const CombatLogPanel: React.FC<CombatLogPanelProps> = ({ isOpen, onClose }) => {
  const [entries, setEntries] = useState<DebugFCTEntry[]>([]);
  const [enabledChannels, setEnabledChannels] = useState<Set<FCTChannel>>(new Set(ALL_CHANNELS));
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  // A closed diagnostics panel must not consume every combat event and render
  // a hidden row tree. Refresh from DebugFCT's bounded history when opened,
  // then subscribe only for the visible lifetime of the panel.
  useEffect(() => {
    if (!isOpen) return undefined;
    setEntries(DebugFCT.getHistory());
    return DebugFCT.subscribe((entry) => {
      setEntries(prev => {
        const next = [...prev, entry];
        return next.length > 200 ? next.slice(-200) : next;
      });
    });
  }, [isOpen]);

  // Auto-scroll
  useEffect(() => {
    if (isOpen && autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries, autoScroll, isOpen]);

  const toggleChannel = useCallback((ch: FCTChannel) => {
    setEnabledChannels(prev => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch); else next.add(ch);
      return next;
    });
  }, []);

  const copyLog = useCallback(() => {
    const text = DebugFCT.copyHistory(50);
    navigator.clipboard.writeText(text).then(
      () => { /* silent success */ },
      () => gameLogger.warn('ui', 'Failed to copy combat log')
    );
  }, []);

  const clearLog = useCallback(() => setEntries([]), []);

  if (!isOpen) return null;

  const filtered = entries.filter(e => enabledChannels.has(e.channel));

  const btnStyle: React.CSSProperties = {
    padding: '2px 8px',
    fontSize: 11,
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 3,
    background: 'rgba(255,255,255,0.05)',
    color: '#aaa',
    cursor: 'pointer',
  };

  return (
    <ToolWindow
      id="combat-log"
      title="Combat Log"
      icon="📜"
      isOpen={isOpen}
      onClose={onClose}
      defaultPosition={{ x: 20, y: 300 }}
      defaultSize={{ width: 380, height: 320 }}
      minSize={{ width: 280, height: 200 }}
      collapsible
      resizable
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 4, fontSize: 12 }}>
        {/* Channel filters + actions */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '2px 0' }}>
          {ALL_CHANNELS.map(ch => (
            <button
              key={ch}
              type="button"
              onClick={() => toggleChannel(ch)}
              style={{
                ...btnStyle,
                background: enabledChannels.has(ch) ? 'rgba(255,255,255,0.15)' : 'transparent',
                color: enabledChannels.has(ch) ? CHANNEL_COLORS[ch] : '#555',
                borderColor: enabledChannels.has(ch) ? CHANNEL_COLORS[ch] : 'rgba(255,255,255,0.1)',
              }}
            >
              {CHANNEL_LABELS[ch]}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          <button type="button" onClick={copyLog} style={btnStyle} title="Copy last 50 entries">Copy</button>
          <button type="button" onClick={clearLog} style={btnStyle}>Clear</button>
        </div>

        {/* Log entries */}
        <div
          ref={scrollRef}
          onScroll={(e) => {
            const el = e.currentTarget;
            const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
            setAutoScroll(atBottom);
          }}
          style={{
            flex: 1,
            overflowY: 'auto',
            fontFamily: 'monospace',
            fontSize: 11,
            lineHeight: '16px',
            background: 'rgba(0,0,0,0.3)',
            borderRadius: 4,
            padding: 4,
          }}
        >
          {filtered.length === 0 && (
            <div style={{ color: '#555', textAlign: 'center', padding: 20 }}>
              No log entries yet. Cast abilities or attack mobs.
            </div>
          )}
          {filtered.map((e) => (
            <LogRow key={e.id} entry={e} />
          ))}
        </div>

        {/* Status bar */}
        <div style={{ fontSize: 10, color: '#555', display: 'flex', justifyContent: 'space-between' }}>
          <span>{filtered.length} entries ({entries.length} total)</span>
          <span>{autoScroll ? 'auto-scroll ON' : 'scroll paused'}</span>
        </div>
      </div>
    </ToolWindow>
  );
};
